import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Avatar,
  Rating,
  Paper,
  Modal,
  IconButton,
  CardMedia,
} from '@mui/material';
import {
  AutoAwesome,
  Groups,
  CloudUpload,
  Share,
  Analytics,
  CheckCircle,
  ArrowForward,
  LocalOffer,
  Description,
  Search,
  Close,
  ZoomIn,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import contentLibraryImage from './content-library-search.jpg';
import tagOrganizationImage from './tag-organization-diagram.jpg';
import salesKitImage from './product-sales-kit.jpg';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

  const handleImageClick = (src: string, alt: string) => {
    setModalImage({ src, alt });
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
    setModalImage(null);
  };

  const features = [
    {
      icon: <Search />,
      title: 'Powerful Search & Discovery',
      description: 'Find content instantly with smart search across keywords, tags, ratings, file types, and bookmarks. Multi-attribute filtering makes discovery effortless.',
    },
    {
      icon: <LocalOffer />,
      title: 'Flexible Tag-Based Organization',
      description: 'Multi-dimensional tagging with tag groups surpasses simple folders. Include content in multiple categories simultaneously. Ordered and unordered groups support any workflow.',
    },
    {
      icon: <CloudUpload />,
      title: 'Revolutionary Cost Efficiency',
      description: 'Concurrent user pricing: 10 seats serve 100+ users. Perfect for teams with mixed usage intensity. Achieve 80-90% cost reduction vs. traditional per-seat licensing.',
    },
    {
      icon: <Groups />,
      title: 'Multi-Company Team Support',
      description: 'Built for teams spanning multiple organizations. Join unlimited teams with one login. Team managers easily invite or remove members across company boundaries.',
    },
    {
      icon: <Share />,
      title: 'Secure Public Sharing',
      description: 'Share content outside your team with feature-rich links: expiration dates, passwords, usage limits, revocation, and monitoring. Publishers control shareability per item.',
    },
    {
      icon: <Analytics />,
      title: 'Simplified Administration',
      description: 'Organizations manage multiple teams and subscriptions in one interface. Share subscriptions across teams for maximum efficiency. Separate billing from team management.',
    },
  ];

  const perfectFor = [
    {
      title: 'Sales Enablement',
      description: 'Publish presentations, case studies, and product materials to your sales team. Share proposals and collateral with prospects through controlled public links.',
      benefits: ['Centralized sales content', 'Controlled external sharing', 'Quick content discovery'],
    },
    {
      title: 'Agency Client Portals',
      description: 'Create dedicated portals for each client. Publish deliverables, brand assets, and campaign materials with organized access across agency and client teams.',
      benefits: ['Multi-organization support', 'Client-specific content', 'Professional presentation'],
    },
    {
      title: 'HR & Policy Distribution',
      description: 'Publish employee handbooks, policy documents, and training materials. Organize by department, topic, and employee type for easy discovery.',
      benefits: ['Searchable policy library', 'Cost-efficient concurrent users', 'Instant file previews'],
    },
    {
      title: 'Partner Distribution Networks',
      description: 'Share product information, marketing materials, and documentation across multi-tier distribution channels. Use shared teams or separate teams per partner organization.',
      benefits: ['Multi-tier sharing', 'Flexible team structures', 'Centralized updates'],
    },
    {
      title: 'Member Organizations',
      description: 'Publish resources, event materials, and communications to club members, associations, or community groups with searchable, organized content.',
      benefits: ['Member resource library', 'Custom curated views', 'Flexible tagging'],
    },
    {
      title: 'IT Service Portals',
      description: 'Publish technical documentation, software guides, and support resources. Enable IT teams and end users to find answers quickly with powerful search and bookmarks.',
      benefits: ['Knowledge base publishing', 'Quick reference guides', 'User feedback ratings'],
    },
  ];

  const stats = [
    { value: '80-90%', label: 'Cost Reduction' },
    { value: '10:1', label: 'User to Seat Ratio' },
    { value: 'Unlimited', label: 'Tags Per Item' },
    { value: 'Simple', label: 'Administration' },
  ];

  const useCases = [
    'Product Catalogs',
    'Sales Presentations',
    'Training Materials',
    'Marketing Assets',
    'Technical Documentation',
    'Customer Resources',
    'Brand Guidelines',
    'Proposal Templates',
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Chip
                  label="Simple • Powerful • Affordable"
                  color="primary"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    fontWeight: 800,
                    mb: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Effective Content Sharing Made Simple
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{ mb: 4, fontWeight: 400 }}
                >
                  Share content effortlessly across teams and organizations. Powerful tag-based organization, flexible search, multi-company support, and revolutionary concurrent user pricing that's 80-90% less expensive.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                  <Button
                    component={Link}
                    to="/signup"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 1.5,
                      px: 4,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      },
                    }}
                  >
                    Start Free Forever
                  </Button>
                  <Button
                    component={Link}
                    to="/features"
                    variant="outlined"
                    size="large"
                    sx={{ py: 1.5, px: 4 }}
                  >
                    Explore Features
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  No credit card required • Free forever • Upgrade anytime
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: 6,
                    border: `3px solid ${theme.palette.primary.main}20`,
                  }}
                >
                  <img
                    src={contentLibraryImage}
                    alt="Advanced content search interface showing grid view of diverse content types including PDFs, images, videos, and documents with powerful filtering by file type, tags, bookmarks, and ratings. Results display with metadata, privacy indicators, and engagement metrics."
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={3}>
          {stats.map((stat) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Advantage Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.dark} 100%)`,
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">

            {/* Left: copy + CTA */}
            <Grid item xs={12} md={5}>
              <Chip
                label="Revolutionary Pricing Model"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, mb: 2 }}
              />
              <Typography
                variant="h2"
                sx={{ color: '#fff', fontWeight: 800, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
              >
                Stop paying for seats that are empty
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.7 }}>
                Most distributed teams have only <strong style={{ color: '#fff' }}>8–18% of members active at the same
                time</strong>. Per-seat tools charge you for 100% of your roster regardless. Concurrent user pricing
                means you only pay for the seats actually in use — and the savings are dramatic.
              </Typography>
              <Box sx={{ mb: 2 }}>
                {[
                  'Add unlimited team members at no extra cost',
                  'One subscription spans multiple companies',
                  '$0 setup fee — live the same day',
                ].map(item => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle sx={{ color: theme.palette.secondary.light, fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                component={Link}
                to="/compare"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  background: '#ffffff',
                  color: theme.palette.primary.dark,
                  '&:hover': { background: 'rgba(255,255,255,0.92)' },
                }}
              >
                Calculate your team's savings
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(255,255,255,0.6)' }}>
                Compare vs. Highspot, Seismic, Showpad &amp; more
              </Typography>
            </Grid>

            {/* Right: cost comparison visual */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 2 }}
              >
                Example: 100-person team, mixed usage
              </Typography>
              <Grid container spacing={2}>

                {/* Per-seat card */}
                <Grid item xs={12} sm={6}>
                  <Card sx={{ bgcolor: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
                        Typical per-seat tool
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                        Highspot / Seismic
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ color: '#FC8181', lineHeight: 1 }}
                      >
                        $78,000
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>per year</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', lineHeight: 1.5 }}>
                        $65/seat/mo × 100 users × 12 mo
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                        + up to $25,000 setup fee
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                        + 4–8 weeks to go live
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* T1C card */}
                <Grid item xs={12} sm={6}>
                  <Card sx={{ bgcolor: 'rgba(20,184,166,0.15)', border: `2px solid ${theme.palette.secondary.main}`, height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Chip
                        label="✦ Recommended"
                        size="small"
                        sx={{ bgcolor: theme.palette.secondary.main, color: '#fff', fontWeight: 700, mb: 1, fontSize: 10 }}
                      />
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                        Tier One Content
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ color: '#5EEAD4', lineHeight: 1 }}
                      >
                        $7,188
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>per year</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', lineHeight: 1.5 }}>
                        18 concurrent users (Medium plan)
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                        $0 setup fee
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                        Live the same day
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Savings callout */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      borderRadius: 2,
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#7EC8F8', lineHeight: 1 }}>
                        Save $70,812/year
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                        91% less than per-seat pricing — with unlimited total members
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 220, lineHeight: 1.5 }}
                    >
                      Based on typical mid-market Highspot pricing. Actual savings vary by team size and usage pattern.
                    </Typography>
                  </Box>
                </Grid>

              </Grid>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
              Everything You Need for Effective Content Sharing
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Simple, powerful tools that make organizing and sharing content effortless
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: `${theme.palette.primary.main}15`,
                        color: 'primary.main',
                        width: 64,
                        height: 64,
                        mb: 3,
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Visual Feature Showcase Section - Custom Views */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            Create Curated Content Experiences with Custom Views
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Build beautiful, organized pages that bring your content together—perfect for sales kits, resource centers, and product showcases
          </Typography>
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 0,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardMedia
            component="img"
            image={salesKitImage}
            alt="Custom View Example - Product Sales Kit"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </Paper>
      </Container>

      {/* Use Cases Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            Perfect for Any Type of Content
          </Typography>
          <Typography variant="h6" color="text.secondary">
            From documents to videos—share any content type your team needs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {useCases.map((useCase) => (
            <Chip
              key={useCase}
              label={useCase}
              size="medium"
              sx={{
                py: 3,
                px: 2,
                fontSize: '1rem',
                backgroundColor: 'background.default',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            />
          ))}
        </Box>
      </Container>

      {/* Tag-Based Organization Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                Multi-Dimensional Tagging
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Far more powerful than folders—organize content in unlimited ways
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
                Traditional folders force you to pick one category. With tag groups, you can organize the same content by product line, target market, sales stage, and more—all at once.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
                Create ordered tag groups for process steps (like a sales funnel) or unordered groups for classifications (like industries or applications). Each content item appears in all relevant views automatically.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 6,
                  border: `3px solid ${theme.palette.primary.main}20`,
                  p: '1px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 10,
                    '& .zoom-icon': {
                      opacity: 1,
                    },
                  },
                }}
                onClick={() =>
                  handleImageClick(
                    tagOrganizationImage,
                    'Comparison diagram showing folders vs tags: Folders are rigid and hierarchical with single document location, while tags are flexible and multi-dimensional allowing unlimited categorization and flexible retrieval by any combination of tags'
                  )
                }
              >
                <Box
                  className="zoom-icon"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out',
                    zIndex: 1,
                  }}
                >
                  <ZoomIn sx={{ color: 'white' }} />
                </Box>
                <img
                  src={tagOrganizationImage}
                  alt="Comparison diagram showing folders vs tags: Folders are rigid and hierarchical with single document location, while tags are flexible and multi-dimensional allowing unlimited categorization and flexible retrieval by any combination of tags"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Perfect For Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              Controlled Publishing for Every Use Case
            </Typography>
            <Typography variant="h6" color="text.secondary">
              From internal teams to multi-tier distribution—publish and share content with complete control
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {perfectFor.map((useCase) => (
              <Grid item xs={12} md={4} key={useCase.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    {useCase.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    {useCase.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {useCase.benefits.map((benefit) => (
                      <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 10,
          color: 'white',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Ready to Transform How You Share Content?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Join teams reducing costs by 80-90% with simple, powerful content sharing
          </Typography>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              background: '#ffffff',
              color: theme.palette.primary.dark,
              fontWeight: 700,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.92)',
              },
            }}
          >
            Start Free Forever
          </Button>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              <Typography>Free forever</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              <Typography>No credit card required</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              <Typography>Upgrade anytime</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Image Modal */}
      <Modal
        open={imageModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '95vw',
            maxHeight: '95vh',
            outline: 'none',
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: -16,
              right: -16,
              bgcolor: 'background.paper',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <Close />
          </IconButton>
          <Box
            sx={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              overflow: 'auto',
              borderRadius: 2,
              boxShadow: 24,
              bgcolor: 'background.paper',
              p: 1,
            }}
          >
            {modalImage && (
              <img
                src={modalImage.src}
                alt={modalImage.alt}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default HomePage;